import * as React from "react";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import {none, option, Option} from "ts-option";
import GoCardlessPayout from "../../model/transaction/GoCardlessPayout";
import {TransactionEventType} from "../../model/transaction/event/TransactionEvent";
import AccountingNote from "../../model/invoice/note/AccountingNote";
import {api} from "../../api/api";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import Loading from "../ui/loading/Loading";
import {CommonDetailsStatusRow} from "./common/CommonDetailsStatusRow";
import {CommonDetailsInfoRowType, default as CommonDetailsInfoRow, DetailsInfoRowItem} from "./common/CommonDetailsInfoRow";
import DateUtils from "../../utils/DateUtils";
import TransactionActivityTabs from "./transactionActivityTabs/TransactionActivityTabs";
import TransactionActivityStream from "./transactionActivityStream/TransactionActivityStream";
import ReconciliationModal from "../v5/table/presenter/reconciliation/ReconciliationModal";
import DataGrid from "../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import CurrencyHelper from "../../helper/CurrencyHelper";
import LinkPresenter from "./LinkPresenter";
import {
	ReconciliationStatusPresenterComponent,
	ReconciliationValue
} from "../v5/table/presenter/reconciliation/ReconciliationStatusPresenter";
import TransactionStatusPresenter from "./TransactionStatusPresenter";
import Routes from "../../navigation/route/Route";
import {CommonDetailsSummary, DetailsSummaryItem} from "./common/CommonDetailsSummary";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import IconSmall from "../../hiberUiKit/src/atoms/Icon/IconSmall";
import Transaction, {ReconciliationStatus} from "../../model/transaction/Transaction";
import IgnoreTransactionModal from "../v5/table/presenter/IgnoreTransactionModal";
import "./stripePayoutDetailsView.scss";
import * as classNames from "classnames";

interface RouteProps {
	/**
	 * GoCardless payout internal identifier.
	 */
	goCardlessPayoutId: string;
}

interface State {
	/**
	 * GoCardless payout transaction.
	 */
	goCardlessPayout: Option<GoCardlessPayout>;
	/**
	 * GoCardless payout activity events.
	 */
	events: TransactionEventType[];
	/**
	 * GoCardless payout notes.
	 */
	notes: AccountingNote[];
	/**
	 * Note selected for edition.
	 */
	noteToEdit: Option<AccountingNote>;
	/**
	 * Reconciliation modal visibility indicator.
	 */
	reconciliationModalVisible: boolean;
	/**
	 * If true, table row click callback is called.
	 */
	isTableRowClickable: boolean;
	/**
	 * Index of transaction displayed inside reconciliation modal (opened with GoCardless payout status button).
	 */
	transactionIndex: number;
	/**
	 * If defined, transaction ignore/unignore modal is displayed.
	 */
	transactionToIgnore: Option<Transaction>;
}

type Props = RouteComponentProps<RouteProps>;

/**
 * GoCardless payout details view.
 */
class GoCardlessPayoutDetailsView extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			goCardlessPayout: none,
			events: [],
			notes: [],
			noteToEdit: none,
			reconciliationModalVisible: false,
			isTableRowClickable: true,
			transactionIndex: 0,
			transactionToIgnore: none
		};
	}

	componentDidMount(): void {
		this.fetchGoCardlessPayout();
	}

	fetchGoCardlessPayout = () => api.instance.transactionApi.fetchGoCardlessPayout(
		this.props.match.params.goCardlessPayoutId).then(
		goCardlessPayout => {
			this.setState({goCardlessPayout: option(goCardlessPayout)});
			this.fetchEvents(goCardlessPayout.id);
			this.fetchNotes(goCardlessPayout.id);
		}).catch(error => {
		notifyBugsnagAndShowToast(error, "Fetch GoCardless payout");
		this.props.history.goBack();
	});

	fetchEvents = (transactionId: string) => {
		api.instance.transactionApi.fetchTransactionEvents(transactionId)
			.then(events => this.setState({events}))
			.catch(error => {
				notifyBugsnagAndShowToast(error, "Fetch GoCardless payout events");
				throw error;
			})

	};

	fetchNotes = (transactionId: string) => {
		api.instance.noteApi.fetchTransactionNotes(transactionId)
			.then(notes => this.setState({notes}))
			.catch(error => {
				notifyBugsnagAndShowToast(error, "Fetch GoCardless payout notes");
				throw error;
			})
	};

	render() {
		const reconciliationModalNavigationLabel =
			`${this.state.transactionIndex + 1}/${this.state.goCardlessPayout.map(sp => sp.transactions.length).getOrElse(0)}
			GoCardless transaction`;
		return (
			<div className={"l-page"}>
				<Loading isLoading={this.state.goCardlessPayout.isEmpty}>
					<CommonDetailsStatusRow title={"GoCardless transaction"}
											onBackClick={this.goBack}
											refNumPath={["All transactions", "GoCardless payout"]}
											additionalComponent={this.renderStatus()}/>
					{this.renderDetailsInfoRow()}
					{this.renderTable()}
					<div className="c-common-details_common-summary_external-container">
						{this.renderSummary()}
					</div>
					<div className="c-common-details_activity-tabs-container">
						<TransactionActivityTabs transaction={this.state.goCardlessPayout}
												 noteToEdit={this.state.noteToEdit}
												 onAddNoteSuccess={this.onAddNoteSuccess}
												 onUpdateNoteSuccess={this.onUpdateNoteSuccess}/>
					</div>
					{this.state.goCardlessPayout.map(sp => <TransactionActivityStream transaction={sp}
																					  activeNoteId={this.props.location.state ?
																						  this.props.location.state.activeId : ""}
																					  events={this.state.events}
																					  notes={this.state.notes}
																					  onEditNoteClick={this.onEditNoteClick}
																					  onDeleteNoteSuccess={this.onDeleteNoteSuccess}/>).orNull}
					{this.state.reconciliationModalVisible && this.state.goCardlessPayout.map(t => {
						return <ReconciliationModal transaction={t.transactions[this.state.transactionIndex]}
													onClose={this.closeReconciliationModal}
													onReconciliationSuccess={this.fetchGoCardlessPayout}
													readOnly={true}
													showNavigation={true}
													onNavigateLeft={this.onLeftNavigationButtonClick}
													onNavigateRight={this.onRightNavigationButtonClick}
													navigateLeftDisabled={this.leftNavigationButtonDisabled()}
													navigateRightDisabled={this.rightNavigationButtonDisabled()}
													navigationLabel={reconciliationModalNavigationLabel}/>
					}).orNull}
					{this.state.transactionToIgnore.map(this.renderIgnoreModal).orNull}
				</Loading>
			</div>
		);
	}

	goBack = () => {
		if (this.props.location.state) {
			this.props.history.push(Routes.AllTransactionsView, {pageNo: this.props.history.location.state["pageNo"]});
		}
		else {
			this.props.history.push(Routes.AllTransactionsView);
		}
	};

	renderDetailsInfoRow = () => {
		return this.state.goCardlessPayout.map(sp => {
			const detailsInfoRowItem1: DetailsInfoRowItem = {
				label: "Time",
				value: sp.dateTime.format(DateUtils.DATE_FORMAT)
			};
			const detailsInfoRowItem2: DetailsInfoRowItem = {
				label: "GoCardless Payout ID",
				value: sp.goCardlessPayoutId,
				url: sp.url,
				itemType: CommonDetailsInfoRowType.Link
			};
			const detailsInfoRowItem3: DetailsInfoRowItem = {
				label: "Fee",
				value: CurrencyHelper.toCurrency(sp.fee)
			};
			return <CommonDetailsInfoRow items={[detailsInfoRowItem1, detailsInfoRowItem2, detailsInfoRowItem3]}/>
		}).orNull;
	};

	renderTable = () => {
		return (
			<DataGrid>
				<thead>
				<tr className="c-common-details_table-header-row">
					<th className="c-common-details_table-desc-cell">GOCARDLESS PAYMENT ID</th>
					<th>DATE</th>
					<th>NAME</th>
					<th>ACCT #</th>
					<th>BRANCH</th>
					<th>COMMENT</th>
					<th>AMOUNT</th>
					<th>DUE</th>
					<th>STATUS</th>
					<th/>
				</tr>
				</thead>
				<tbody>
				{this.state.goCardlessPayout.map(sp => sp.transactions.map((item) => {
						const reconciliationValue: ReconciliationValue = {
							reconcileStatus: item.reconciliationStatus,
							matched: item.matched
						};
						const classFromProps = classNames({
							"c-common-details_table-row": true,
							"clickable": item.visible,
							"ignored": item.reconciliationStatus === ReconciliationStatus.IGNORED
						});
						return (
							<tr className={classFromProps}
								onClick={() => this.state.isTableRowClickable && item.visible && this.onRowClick(item.id)}>
								<td className="c-common-details_table-desc-cell">
									<LinkPresenter label={item.transactionId.orNull} url={item.url.orNull}/>
								</td>
								<td>{item.date.format(DateUtils.DATE_FORMAT)}</td>
								<td>{item.name}</td>
								<td>{item.accountNo.orNull}</td>
								<td>{item.branchCode.orNull}</td>
								<td>{item.comment.orNull}</td>
								<td>{CurrencyHelper.toCurrency(item.amount)}</td>
								<td>{CurrencyHelper.toCurrency(item.due)}</td>
								<td className="c-stripe-payout-details_reconciliation-col">
									{item.visible && <ReconciliationStatusPresenterComponent
										value={reconciliationValue}
										transaction={item}
										onModalOpen={() => this.setState({isTableRowClickable: false})}
										onModalClose={this.onCloseTransactionReconciliationModal}/>}
								</td>
								<td onClick={(event) => item.visible && this.onIgnoreClick(event, item)}>{item.visible &&
								this.renderIgnoreTransactionIcon(item)}</td>
							</tr>
						);
					})
				).orNull}
				</tbody>
			</DataGrid>);
	};

	renderIgnoreTransactionIcon = (transaction: Transaction) => {
		if (transaction.reconciliationStatus === ReconciliationStatus.RECONCILED) {
			return null;
		}
		const icon = transaction.reconciliationStatus === ReconciliationStatus.IGNORED ? Icon.Reset : Icon.X;
		return <IconSmall icon={icon} variant={"gray"} customClass="c-stripe-payout-details_ignore"/>;
	};

	onIgnoreClick = (event: React.MouseEvent<HTMLDivElement>, transaction: Transaction) => {
		event.stopPropagation();
		this.setState({transactionToIgnore: option(transaction)});
	};

	renderIgnoreModal = (transaction: Transaction) => {
		return (
			<IgnoreTransactionModal transaction={transaction}
									onClose={() => this.setState({transactionToIgnore: none})}
									onSuccess={this.onIgnoreSuccess}/>
		);
	};

	onIgnoreSuccess = () => {
		this.setState({transactionToIgnore: none});
		this.fetchGoCardlessPayout();
	};

	onCloseTransactionReconciliationModal = () => {
		this.setState({isTableRowClickable: true}, this.fetchGoCardlessPayout);
	};

	renderSummary = () => {
		return this.state.goCardlessPayout.map(sp => {
			const lastSummaryItem: DetailsSummaryItem = {
				label: "Amount to reconcile",
				value: CurrencyHelper.toCurrency(sp.due)
			};
			return (
				<CommonDetailsSummary items={[]}
									  totalItem={lastSummaryItem}
									  style={{marginRight: 250}}
									  extraWidth={true}/>
			);
		}).orNull
	};

	renderStatus = () => this.state.goCardlessPayout.map(t =>
		<TransactionStatusPresenter transaction={t} onClick={this.openReconciliationModal} disableReconciliation={true}/>).orNull;

	onRowClick = (transactionId: string) => this.props.history.push(Routes.TransactionDetailsView(transactionId));

	openReconciliationModal = () => this.setState({reconciliationModalVisible: true});

	closeReconciliationModal = () => this.setState({reconciliationModalVisible: false});

	onLeftNavigationButtonClick = () => this.setState({transactionIndex: this.state.transactionIndex - 1});

	onRightNavigationButtonClick = () => this.setState({transactionIndex: this.state.transactionIndex + 1});

	leftNavigationButtonDisabled = () => this.state.transactionIndex === 0;

	rightNavigationButtonDisabled = () => this.state.transactionIndex ===
		this.state.goCardlessPayout.map(sp => sp.transactions.length - 1).getOrElse(-1);

	onAddNoteSuccess = (note: AccountingNote) => this.setState({notes: this.state.notes.concat(note)});

	onUpdateNoteSuccess = (note: AccountingNote) => this.setState({
		notes: this.state.notes.filter(n => n.id !== note.id).concat(note),
		noteToEdit: none
	});

	onEditNoteClick = (note: AccountingNote) => this.setState({noteToEdit: option(note)});

	onDeleteNoteSuccess = (note: AccountingNote) => {
		this.setState({noteToEdit: none, notes: this.state.notes.filter(n => n.id !== note.id)});
	};
}

export default withRouter(GoCardlessPayoutDetailsView)
